import { useState } from 'react'
import type { TrackingList } from '../../types/finance'
import { FAVORITES_LIST_ID, useWatchlists } from '../../lib/watchlists-context'
import Lightbox from '../Lightbox'

// One card for every watchlist. Favorites is just the default list (the star
// on a quote writes to it); creating, renaming, and deleting lists lives in
// the Manage lightbox.

type SortMode = 'added' | 'change' | 'beta'

export default function WatchlistsCard({ onSelect }: { onSelect: (symbol: string) => void }) {
  const {
    lists,
    activeList,
    setActiveListId,
    quotes,
    pending,
    addSymbol,
    removeSymbol,
  } = useWatchlists()
  const [sortBy, setSortBy] = useState<SortMode>('added')
  const [addText, setAddText] = useState('')
  const [manageOpen, setManageOpen] = useState(false)

  const symbols = [...(activeList?.symbols ?? [])]
  if (sortBy !== 'added') {
    symbols.sort((a, b) => {
      const qa = quotes[a]
      const qb = quotes[b]
      if (sortBy === 'beta') return (qb?.trailingBeta ?? -Infinity) - (qa?.trailingBeta ?? -Infinity)
      return Math.abs(qb?.changePercent ?? 0) - Math.abs(qa?.changePercent ?? 0)
    })
  }

  function submitAdd() {
    if (!activeList || !addText.trim()) return
    addSymbol(activeList.id, addText)
    setAddText('')
  }

  const sortBtn = (mode: SortMode, label: string) => (
    <button
      onClick={() => setSortBy(mode)}
      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
        sortBy === mode
          ? 'bg-slate-300 dark:bg-slate-600 text-slate-900 dark:text-white font-medium'
          : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-slate-900 dark:text-white font-semibold text-sm">Watchlists</h2>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 dark:text-slate-600 text-xs">Sort:</span>
          <div className="flex bg-slate-200/60 dark:bg-slate-700/60 rounded-lg p-0.5">
            {sortBtn('added', 'Added')}
            {sortBtn('change', '% Move')}
            {sortBtn('beta', '12M Beta')}
          </div>
          <span className="text-slate-500 text-xs ml-1">
            {activeList?.symbols.length ?? 0} equities
          </span>
        </div>
      </div>

      {/* List tabs + manage */}
      <div className="px-5 py-2.5 border-b border-slate-200/50 dark:border-slate-700/50 flex flex-wrap items-center gap-1.5">
        {lists.map((l) => (
          <button
            key={l.id}
            onClick={() => setActiveListId(l.id)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              activeList?.id === l.id
                ? 'bg-blue-600 text-white'
                : 'bg-slate-200/70 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {l.id === FAVORITES_LIST_ID ? '⭐ ' : ''}
            {l.name}
          </button>
        ))}
        <button
          onClick={() => setManageOpen(true)}
          className="ml-auto px-2.5 py-1 rounded-full text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 transition-colors"
        >
          ⚙ Manage lists
        </button>
      </div>

      {/* Add symbol to the active list */}
      <div className="px-5 py-2.5 border-b border-slate-200/50 dark:border-slate-700/50 flex items-center gap-2">
        <input
          value={addText}
          onChange={(e) => setAddText(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && submitAdd()}
          placeholder={`Add symbol to ${activeList?.name ?? 'list'} (e.g. NVDA)`}
          className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-xs rounded-lg px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none w-64 max-w-full"
        />
        <button
          onClick={submitAdd}
          disabled={!addText.trim()}
          className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 dark:disabled:bg-slate-700 disabled:text-slate-500 text-white transition-colors"
        >
          Add
        </button>
      </div>

      {symbols.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-4xl mb-3">{activeList?.id === FAVORITES_LIST_ID ? '⭐' : '📋'}</div>
          <p className="text-slate-900 dark:text-white font-medium text-sm mb-1">
            {activeList?.name ?? 'This list'} is empty
          </p>
          <p className="text-slate-500 dark:text-slate-400 text-sm max-w-sm mx-auto">
            Add a symbol above, or open a security and tap the star / "Add to list" to save it here.
            Lists persist across sessions.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-5 py-2 bg-white/80 dark:bg-slate-800/80 border-b border-slate-200/50 dark:border-slate-700/50 text-xs text-slate-500">
            <span className="flex-1 min-w-0">Symbol</span>
            <div className="flex items-center gap-4 text-right">
              <span className="w-20">Price</span>
              <span className="w-28">Change</span>
              <span className="w-16">12M Beta</span>
              <span className="w-6" />
            </div>
          </div>
          <div className="divide-y divide-slate-200/50 dark:divide-slate-700/50">
            {symbols.map((sym) => {
              const q = quotes[sym]
              const isLoading = pending.has(sym)
              const isPositive = (q?.change ?? 0) >= 0
              return (
                <div
                  key={sym}
                  className="flex items-center justify-between px-5 py-3 hover:bg-slate-200/30 dark:hover:bg-slate-700/30 transition-colors group"
                >
                  <button onClick={() => onSelect(sym)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 dark:text-white font-semibold text-sm">{sym}</span>
                        {isLoading && (
                          <svg className="w-3 h-3 animate-spin text-slate-500" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                      </div>
                      <p className="text-slate-500 text-xs truncate">{q?.name ?? '—'}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-4">
                    <div className="w-20 text-right">
                      <p className="text-slate-900 dark:text-white font-medium text-sm">
                        {q ? `$${q.price.toFixed(2)}` : '—'}
                      </p>
                    </div>
                    <div className="w-28 text-right">
                      {q ? (
                        <span
                          className={`text-xs font-medium ${
                            isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {isPositive ? '+' : ''}
                          {q.change.toFixed(2)} ({isPositive ? '+' : ''}
                          {q.changePercent.toFixed(2)}%)
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </div>
                    <div className="w-16 text-right">
                      <span
                        className={`text-xs font-medium ${
                          q?.trailingBeta != null && q.trailingBeta > 1.5
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {q?.trailingBeta != null ? q.trailingBeta.toFixed(2) : 'N/A'}
                      </span>
                    </div>
                    <button
                      onClick={() => activeList && removeSymbol(activeList.id, sym)}
                      className="w-6 p-1 text-slate-400 dark:text-slate-600 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                      title={`Remove ${sym} from ${activeList?.name ?? 'list'}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      <ManageListsLightbox open={manageOpen} onClose={() => setManageOpen(false)} />
    </div>
  )
}

function ManageListsLightbox({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lists, createList, renameList, deleteList, setActiveListId } = useWatchlists()
  const [newName, setNewName] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameText, setRenameText] = useState('')

  function submitCreate() {
    const id = createList(newName)
    if (id) {
      setActiveListId(id)
      setNewName('')
    }
  }

  function commitRename(list: TrackingList) {
    renameList(list.id, renameText)
    setRenamingId(null)
  }

  return (
    <Lightbox open={open} onClose={onClose} maxWidth="max-w-lg">
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-slate-900 dark:text-white font-semibold text-sm">Manage watchlists</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Favorites is the default list — the ⭐ on any security saves there. It can't be renamed or deleted.
          </p>
        </div>

        <div className="px-5 py-3 border-b border-slate-200/50 dark:border-slate-700/50 flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitCreate()}
            placeholder="New list name (e.g. Earnings watch)"
            className="flex-1 bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-xs rounded-lg px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
          />
          <button
            onClick={submitCreate}
            disabled={!newName.trim()}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 dark:disabled:bg-slate-700 disabled:text-slate-500 text-white transition-colors"
          >
            Create
          </button>
        </div>

        <div className="divide-y divide-slate-200/50 dark:divide-slate-700/50">
          {lists.map((l) => {
            const isFavorites = l.id === FAVORITES_LIST_ID
            return (
              <div key={l.id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1 min-w-0">
                  {renamingId === l.id ? (
                    <input
                      autoFocus
                      value={renameText}
                      onChange={(e) => setRenameText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(l)
                        if (e.key === 'Escape') setRenamingId(null)
                      }}
                      onBlur={() => commitRename(l)}
                      className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2 py-1 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none w-full"
                    />
                  ) : (
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {isFavorites ? '⭐ ' : ''}
                      {l.name}
                    </p>
                  )}
                  <p className="text-xs text-slate-500">
                    {l.symbols.length} {l.symbols.length === 1 ? 'symbol' : 'symbols'}
                    {l.symbols.length > 0 && ` — ${l.symbols.slice(0, 6).join(', ')}${l.symbols.length > 6 ? '…' : ''}`}
                  </p>
                </div>
                {!isFavorites && renamingId !== l.id && (
                  <button
                    onClick={() => {
                      setRenamingId(l.id)
                      setRenameText(l.name)
                    }}
                    className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    Rename
                  </button>
                )}
                {!isFavorites && (
                  <button
                    onClick={() => {
                      if (
                        l.symbols.length === 0 ||
                        window.confirm(`Delete list "${l.name}" and its ${l.symbols.length} symbols?`)
                      ) {
                        deleteList(l.id)
                      }
                    }}
                    className="text-xs text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </Lightbox>
  )
}
